// src/pages/CustomerDirectory.js — Admin: view all customers
import React, { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import { useAuth } from "../AuthContext";
import Navbar from "../components/Navbar";
import { ADMIN_UID, formatTime, formatFullDate } from "../config";
import { useToast } from "../components/Toast";
import { collection, query, orderBy, onSnapshot, doc, setDoc, addDoc, getDoc, Timestamp } from "firebase/firestore";

export default function CustomerDirectory() {
  const { user } = useAuth();
  const toast = useToast();
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [sortBy, setSortBy] = useState("revenue");
  const [userBalances, setUserBalances] = useState({});
  const [showCreditModal, setShowCreditModal] = useState(null);
  const [creditAmount, setCreditAmount] = useState("");
  const [creditNote, setCreditNote] = useState("");
  const [creditType, setCreditType] = useState("add");
  const isAdmin = user?.uid === ADMIN_UID;

  useEffect(() => {
    if (!isAdmin) return;
    const q = query(collection(db, "orders"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    const q = query(collection(db, "users"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [isAdmin]);

  // Load balances from users collection
  useEffect(() => {
    if (!isAdmin || users.length === 0) return;
    users.forEach(async (u) => {
      const uid = u.uid || u.id;
      const bal = parseFloat(u.balance) || 0;
      setUserBalances(prev => ({ ...prev, [uid]: bal }));
    });
  }, [isAdmin, users]);

  const handleCreditUser = async () => {
    if (!showCreditModal) return;
    const amount = parseFloat(creditAmount);
    if (!amount || isNaN(amount) || amount <= 0) return toast.error("Enter a valid amount");
    const { uid, name, email } = showCreditModal;
    const currentBalance = userBalances[uid] || 0;
    const newBalance = creditType === "add" ? currentBalance + amount : Math.max(0, currentBalance - amount);
    try {
      await setDoc(doc(db, "users", uid), { balance: newBalance }, { merge: true });
      await addDoc(collection(db, "balance_history"), {
        userId: uid, userName: name || "", userEmail: email || "",
        type: creditType, amount, previousBalance: currentBalance, newBalance,
        note: creditNote || (creditType === "add" ? "Zelle payment received" : "Balance deduction"),
        adminEmail: user?.email || "admin", createdAt: Timestamp.now(),
      });
      setUserBalances(prev => ({ ...prev, [uid]: newBalance }));
      toast.success(`${creditType === "add" ? "Added" : "Deducted"} $${amount.toFixed(2)} — new balance: $${newBalance.toFixed(2)}`);
      setShowCreditModal(null); setCreditAmount(""); setCreditNote(""); setCreditType("add");
    } catch (e) { console.error(e); toast.error("Failed to update balance"); }
  };

  if (!isAdmin) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#060a14" }}>
        <div className="card" style={{ textAlign: "center", maxWidth: 400 }}>
          <h2 style={{ color: "#ef4444" }}>Access Denied</h2>
        </div>
      </div>
    );
  }

  const customerData = useMemo(() => {
    const map = {};
    // Start from users collection
    users.forEach((u) => {
      map[u.uid || u.id] = {
        uid: u.uid || u.id,
        name: u.displayName || u.email || "Unknown",
        email: u.email || "",
        phone: u.phone || "",
        joined: u.createdAt,
        orders: 0,
        completed: 0,
        revenue: 0,
        totalCost: 0,
        profit: 0,
        lastOrder: null,
        recentOrders: [],
      };
    });
    // Enrich from orders
    orders.forEach((o) => {
      if (!map[o.userId]) {
        map[o.userId] = {
          uid: o.userId,
          name: o.userName || o.userEmail || "Unknown",
          email: o.userEmail || "",
          phone: "",
          joined: o.createdAt,
          orders: 0, completed: 0, revenue: 0, totalCost: 0, profit: 0,
          lastOrder: null, recentOrders: [],
        };
      }
      const c = map[o.userId];
      c.orders++;
      if (o.status === "completed" && o.quotedPrice) {
        c.completed++;
        c.revenue += parseFloat(o.quotedPrice);
        // Add admin cost for profit calculation
        if (o.adminCost) {
          c.totalCost += parseFloat(o.adminCost);
        }
        // Calculate profit
        c.profit = c.revenue - c.totalCost;
      }
      if (!c.lastOrder || (o.createdAt?.toDate && o.createdAt.toDate() > (c.lastOrder?.toDate ? c.lastOrder.toDate() : 0))) {
        c.lastOrder = o.createdAt;
      }
      c.recentOrders.push(o);
    });
    let arr = Object.values(map);
    // Sort
    if (sortBy === "revenue") arr.sort((a, b) => b.revenue - a.revenue);
    else if (sortBy === "orders") arr.sort((a, b) => b.orders - a.orders);
    else if (sortBy === "profit") arr.sort((a, b) => b.profit - a.profit);
    else if (sortBy === "recent") arr.sort((a, b) => {
      const ta = a.lastOrder?.toDate ? a.lastOrder.toDate().getTime() : 0;
      const tb = b.lastOrder?.toDate ? b.lastOrder.toDate().getTime() : 0;
      return tb - ta;
    });
    else arr.sort((a, b) => a.name.localeCompare(b.name));
    // Filter
    if (search.trim()) {
      const s = search.toLowerCase();
      arr = arr.filter((c) => c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s));
    }
    return arr;
  }, [users, orders, sortBy, search]);

  const selectedData = selectedCustomer ? customerData.find((c) => c.uid === selectedCustomer) : null;

  const exportToCSV = (data) => {
    const headers = ["Name", "Email", "Phone", "Orders", "Completed", "Revenue", "Cost", "Profit", "Avg Order Value", "Joined"];
    const rows = data.map((c) => [
      c.name,
      c.email,
      c.phone || "",
      c.orders,
      c.completed,
      c.revenue.toFixed(2),
      c.totalCost.toFixed(2),
      c.profit.toFixed(2),
      c.completed > 0 ? (c.revenue / c.completed).toFixed(2) : "0.00",
      c.joined ? formatFullDate(c.joined) : ""
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `customer-data-${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="page-bg">
      <Navbar />
      <div className="container">
        <div className="animate-in">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
            <div>
              <h1 style={{ fontSize: 28, fontWeight: 800 }}>Customer Directory</h1>
              <p style={{ color: "#64748b", fontSize: 14, marginTop: 4 }}>{customerData.length} customers</p>
            </div>
          </div>

          {/* Search + sort */}
          <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            <div style={{ flex: 1, position: "relative", minWidth: 200 }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#64748b" strokeWidth="2"
                style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)" }}>
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input className="sf-input" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search customers..." style={{ paddingLeft: 40 }} />
            </div>
            <select className="sf-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ width: 160 }}>
              <option value="revenue">Sort by Revenue</option>
              <option value="profit">Sort by Profit</option>
              <option value="orders">Sort by Orders</option>
              <option value="recent">Sort by Recent</option>
              <option value="name">Sort by Name</option>
            </select>
            <button onClick={() => exportToCSV(customerData)} style={{
              padding: "8px 16px",
              background: "#3b82f6",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
              transition: "all 0.2s"
            }} onMouseEnter={(e) => e.target.style.background = "#2563eb"} onMouseLeave={(e) => e.target.style.background = "#3b82f6"}>
              Export CSV
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: selectedData ? "1fr 1fr" : "1fr", gap: 20 }} className="grid-2">
            {/* Customer list */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {customerData.map((c) => (
                <div key={c.uid}
                  className={`card order-card ${selectedCustomer === c.uid ? "stat-card-selected" : ""}`}
                  style={{ padding: 16, cursor: "pointer" }}
                  onClick={() => setSelectedCustomer(selectedCustomer === c.uid ? null : c.uid)}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <p style={{ fontWeight: 700, color: "#e2e8f0", fontSize: 15 }}>{c.name}</p>
                      <p style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>{c.email}</p>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <p style={{ fontWeight: 800, color: "#10b981", fontSize: 16 }}>${c.revenue.toFixed(2)}</p>
                      <p style={{ fontWeight: 700, color: c.profit >= 0 ? "#10b981" : "#ef4444", fontSize: 13 }}>
                        {c.profit >= 0 ? "+" : ""}{c.profit.toFixed(2)}
                      </p>
                      <p style={{ color: "#64748b", fontSize: 11 }}>{c.orders} orders</p>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 12, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, color: "#475569" }}>{c.completed} completed</span>
                    {c.lastOrder && <span style={{ fontSize: 11, color: "#475569" }}>Last: {formatTime(c.lastOrder)}</span>}
                    <span style={{ fontSize: 11, fontWeight: 700,
                      color: (userBalances[c.uid] || 0) > 0 ? "#10b981" : "#ef4444",
                      background: (userBalances[c.uid] || 0) > 0 ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
                      padding: "2px 8px", borderRadius: 6 }}>
                      Bal: ${(userBalances[c.uid] || 0).toFixed(2)}
                    </span>
                    <button className="btn btn-sm" style={{ fontSize: 10, padding: "2px 10px", marginLeft: "auto",
                      background: "rgba(212,160,83,0.1)", color: "#d4a053", border: "1px solid rgba(212,160,83,0.2)" }}
                      onClick={(e) => { e.stopPropagation(); setShowCreditModal({ uid: c.uid, name: c.name, email: c.email }); }}>
                      💰 Credit
                    </button>
                  </div>
                </div>
              ))}
              {customerData.length === 0 && (
                <div className="card empty-state"><p style={{ color: "#64748b" }}>No customers yet.</p></div>
              )}
            </div>

            {/* Customer detail */}
            {selectedData && (
              <div className="animate-slide-up">
                <div className="card" style={{ position: "sticky", top: 80 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                    <div>
                      <h3 style={{ fontSize: 18, fontWeight: 700, color: "#e2e8f0" }}>{selectedData.name}</h3>
                      <p style={{ color: "#64748b", fontSize: 13 }}>{selectedData.email}</p>
                      {selectedData.phone && <p style={{ color: "#64748b", fontSize: 13 }}>{selectedData.phone}</p>}
                    </div>
                    <button onClick={() => setSelectedCustomer(null)} className="chat-close">✕</button>
                  </div>

                  {/* Balance + Credit Button */}
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16,
                    padding: 14, borderRadius: 10, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Account Balance</p>
                      <p style={{ fontSize: 26, fontWeight: 800,
                        color: (userBalances[selectedData.uid] || 0) > 0 ? "#10b981" : "#ef4444" }}>
                        ${(userBalances[selectedData.uid] || 0).toFixed(2)}
                      </p>
                    </div>
                    <button className="btn btn-primary" style={{ padding: "10px 20px" }}
                      onClick={() => setShowCreditModal({ uid: selectedData.uid, name: selectedData.name, email: selectedData.email })}>
                      💰 Add / Deduct Funds
                    </button>
                  </div>

                  {/* Stats */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
                    <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.03)", textAlign: "center" }}>
                      <p style={{ fontSize: 20, fontWeight: 800, color: "#d4a053" }}>{selectedData.orders}</p>
                      <p style={{ fontSize: 10, color: "#64748b" }}>ORDERS</p>
                    </div>
                    <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.03)", textAlign: "center" }}>
                      <p style={{ fontSize: 20, fontWeight: 800, color: "#10b981" }}>${selectedData.revenue.toFixed(2)}</p>
                      <p style={{ fontSize: 10, color: "#64748b" }}>REVENUE</p>
                    </div>
                    <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.03)", textAlign: "center" }}>
                      <p style={{ fontSize: 20, fontWeight: 800, color: "#3b82f6" }}>
                        {selectedData.orders > 0 ? Math.round((selectedData.completed / selectedData.orders) * 100) : 0}%
                      </p>
                      <p style={{ fontSize: 10, color: "#64748b" }}>COMPLETE%</p>
                    </div>
                    <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.03)", textAlign: "center" }}>
                      <p style={{ fontSize: 20, fontWeight: 800, color: selectedData.profit >= 0 ? "#10b981" : "#ef4444" }}>
                        ${selectedData.profit.toFixed(2)}
                      </p>
                      <p style={{ fontSize: 10, color: "#64748b" }}>PROFIT</p>
                    </div>
                  </div>

                  {/* Additional stats row */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                    <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.03)", textAlign: "center" }}>
                      <p style={{ fontSize: 16, fontWeight: 800, color: "#a78bfa" }}>
                        ${selectedData.completed > 0 ? (selectedData.revenue / selectedData.completed).toFixed(2) : "0.00"}
                      </p>
                      <p style={{ fontSize: 10, color: "#64748b" }}>AVG ORDER VALUE</p>
                    </div>
                    <div style={{ padding: 10, borderRadius: 10, background: "rgba(255,255,255,0.03)", textAlign: "center" }}>
                      <p style={{ fontSize: 16, fontWeight: 800, color: "#f59e0b" }}>
                        ${selectedData.totalCost.toFixed(2)}
                      </p>
                      <p style={{ fontSize: 10, color: "#64748b" }}>TOTAL COST</p>
                    </div>
                  </div>

                  {/* Recent orders */}
                  <h4 style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                    Recent Orders
                  </h4>
                  <div style={{ maxHeight: 400, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
                    {selectedData.recentOrders.slice(0, 20).map((o) => (
                      <div key={o.id} style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(0,0,0,0.2)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <span style={{ fontSize: 12, fontFamily: "monospace", color: "#94a3b8" }}>#{o.id.slice(0, 8)}</span>
                          <span className={`badge badge-${o.status}`} style={{ fontSize: 10, padding: "2px 8px", marginLeft: 8 }}>
                            {o.status}
                          </span>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          {o.quotedPrice && <span style={{ color: "#d4a053", fontSize: 13, fontWeight: 700 }}>${o.quotedPrice}</span>}
                          <span style={{ color: "#475569", fontSize: 11, marginLeft: 8 }}>{formatTime(o.createdAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Credit Modal ── */}
      {showCreditModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
          background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
          onClick={() => setShowCreditModal(null)}>
          <div className="card animate-slide-up" style={{ padding: 28, width: 400, maxWidth: "90vw" }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 4 }}>💰 Manage Balance</h3>
            <p style={{ fontSize: 13, color: "#94a3b8", marginBottom: 16 }}>
              {showCreditModal.name || showCreditModal.email}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16,
              padding: 14, borderRadius: 10, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <span style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>Current Balance</span>
              <span style={{ fontSize: 24, fontWeight: 800, marginLeft: "auto",
                color: (userBalances[showCreditModal.uid] || 0) > 0 ? "#10b981" : "#ef4444" }}>
                ${(userBalances[showCreditModal.uid] || 0).toFixed(2)}
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <button className={`btn btn-sm ${creditType === "add" ? "" : "btn-ghost"}`}
                style={creditType === "add" ? { flex: 1, background: "rgba(16,185,129,0.15)", color: "#10b981", border: "1px solid rgba(16,185,129,0.3)" }
                  : { flex: 1 }}
                onClick={() => setCreditType("add")}>
                + Add Funds
              </button>
              <button className={`btn btn-sm ${creditType === "deduct" ? "" : "btn-ghost"}`}
                style={creditType === "deduct" ? { flex: 1, background: "rgba(239,68,68,0.15)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.3)" }
                  : { flex: 1 }}
                onClick={() => setCreditType("deduct")}>
                − Deduct
              </button>
            </div>
            <input className="sf-input" type="number" step="0.01" min="0" placeholder="Amount ($)"
              value={creditAmount} onChange={e => setCreditAmount(e.target.value)}
              style={{ marginBottom: 10, width: "100%" }} />
            <input className="sf-input" placeholder="Note (e.g. Zelle payment received)"
              value={creditNote} onChange={e => setCreditNote(e.target.value)}
              style={{ marginBottom: 16, width: "100%" }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleCreditUser}>
                {creditType === "add" ? "Add Credit" : "Deduct Credit"}
              </button>
              <button className="btn btn-ghost" onClick={() => { setShowCreditModal(null); setCreditAmount(""); setCreditNote(""); }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
